import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import * as actions from 'modules/actions/account';
import _ from 'lodash';
import { Modal, Alert, Space, Input, Empty } from '@bedrock/components';
import NoResults from '@bedrock/components/es/Empty/images/middle/NoResults'
import clx from 'classnames';
import { BuildingFilled, Check } from '@bedrock/icons-react';
import './index.less';
import { WithViewBaseConfigProvider } from '@mpc/common/src/features/PxToRemWrapper';
import { getVendorStorage, setVendorStorage } from '@mpc/common/src/utils';

/**
 * 将树形结构（enterprises -> tenants）扁平化为列表格式
 * @param enterprisesList - 企业列表，每个企业包含 tenants 数组
 * @returns 扁平化后的供应商列表，每个列表项包含 enterprise 和 tenant 的信息
 */
const flattenEnterprisesToList = (enterprisesList: Array<Record<string, any>>): Array<Record<string, any>> => {
  const flattenedList: any[] = [];
  
  enterprisesList.forEach((enterprise: any) => {
    // 遍历每个 enterprise 的 tenants 数组
    if (enterprise.tenants && Array.isArray(enterprise.tenants)) {
      enterprise.tenants.forEach((tenant: any) => {
        flattenedList.push({
          ...enterprise,
          ...tenant,
          tenant_id: tenant.id,
        });
      });
    } else {
      flattenedList.push({
        ...enterprise,
      });
    }
  });
  
  return flattenedList.map(v => {
    // 处理 NEV 新的 前缀的特殊情况
    if (v.vendorId?.indexOf('NEV') > -1) {
      return {
        ...v,
        full_name: `${v.vendorName || v.userName}-${v.tenantName}`
      }
    } else {
      return {
        ...v,
        full_name: `${v.vendorName || v.userName}-${v.vendorId}`
      }
    }
  });
};

const assembleSelectedId = (vendorId: string, tenantCode: string) => {
  return tenantCode ? `${vendorId}-${tenantCode}` : vendorId;
}

interface ChangeSupplierModalProps {
  onChangeVisible: Function;
  visible: boolean;
  showClose: boolean;
  suppliersList: Array<Record<string, any>>;
  prefixCls?: string;
  isOpenNotification: (flag: boolean) => void
}

class ChangeSupplierModal extends React.Component<
  ChangeSupplierModalProps,
  any
> {

  public static defaultProps = {
    showClose: true,
    visible: false,
    prefixCls: 'mpc-external-supplier-choose',
  };

  constructor(props: ChangeSupplierModalProps) {
    super(props);

    this.state = {
      inputValue: '',
      showModal: false,
      submitLoading: false,
      newSuppliersList: [],
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps: Readonly<ChangeSupplierModalProps>, prevState: Readonly<any>): void {
    const { visible, suppliersList } = this.props;
    if (!prevProps.visible && visible) {
      // 将树形结构扁平化为列表格式
      const newSuppliersList = flattenEnterprisesToList(suppliersList);
      let { vendorId, tenantCode } = getVendorStorage();
      let selectedId = tenantCode ? `${vendorId}-${tenantCode}` : vendorId;
      this.setState({
        selectedId: selectedId || null,
        newSuppliersList,
      });
    }
  }

  // componentDidMount(): void {
  //   const { suppliersList } = this.props;
  //   console.log(suppliersList,'suppliersList')
  //   const newSuppliersList = suppliersList.map(v => {
  //     if (v.is_new) {
  //       return {
  //         ...v,
  //         full_name: `${v.full_name}-${v.channel_type_name}`
  //       }
  //     } else {
  //       return {
  //         ...v,
  //         full_name: `${v.full_name}-${v.id}`
  //       }
  //     }
  //   })
  //   this.setState({ newSuppliersList })
  // }

  public handleModalSubmit = () => {
    const { selectedId } = this.state;
    const { onChangeVisible, isOpenNotification } = this.props;
    this.setState({ submitLoading: true });

    let vendorId = null;
    let tenantCode = null;

    if(selectedId?.indexOf('-') > -1){
      vendorId = selectedId.split('-')[0];
      tenantCode = selectedId.split('-')[1];
    } else {
      vendorId = selectedId;
    }

    setVendorStorage(vendorId, tenantCode);

    // 移除 URL 中的 solar-token 参数
    const url = new URL(window.location.href);
    url.searchParams.delete('solar-token');
    // 重定向到首页
    url.hash = ''
    window.history.replaceState({}, '', url.toString());
    // 修改符合ai review 要求
    this.setState({ submitLoading: false });
    if (onChangeVisible) {
      isOpenNotification(true)
      onChangeVisible(false);
    }
    window.location.reload();
  }

  public handleChoose(supplier: any) {
    this.setState({
      selectedId: assembleSelectedId(supplier.vendorId, supplier.tenantCode),
    });
  }

  public handleInputChange(v: string) {
    this.setState({
      inputValue: v,
    });
  }

  public handleClose() {
    const { showClose, onChangeVisible } = this.props;
    if (showClose) {
      onChangeVisible && onChangeVisible(false);
    }
  }

  // public handleSearch() {
  //   const { suppliersList } = this.props;
  //   const { inputValue } = this.state
  //   const filterSuppliers = suppliersList.filter((v: any) => {
  //     return v.full_name.indexOf(inputValue) > -1;
  //   });
  // }

  public render() {
    const { visible, showClose, prefixCls } = this.props;
    const { submitLoading, selectedId, inputValue, newSuppliersList } = this.state;
    const filterSuppliers = newSuppliersList.filter((v: any) => {
      return v.full_name.indexOf(inputValue) > -1;
    });
    return (
      <WithViewBaseConfigProvider>
      <Modal
        title="选择供应商"
        className='change-supplier-modal'
        width={520}
        onOk={() => this.handleModalSubmit()}
        onCancel={() => this.handleClose()}
        confirmLoading={submitLoading}
        visible={visible}
        destroyOnClose
        cancelButtonProps={{
          style: { display: showClose ? 'initial' : 'none' },
        }}
        closable={showClose}
        maskClosable={false}
      >
        <div className={prefixCls}>
          <Alert showIcon type="warning" className={`${prefixCls}-alert`}
            message="系统检测到您的账号关联多家供应商，请选择本次登录的供应商。"
          />
          <Input.Search
            className={`${prefixCls}-input`}
            value={inputValue}
            size='large'
            style={{
              width: '100%',
              marginBottom: 24,
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              this.handleInputChange(e.target.value)
            }}
            placeholder="请输入供应商名称"
          />
          {filterSuppliers?.length ? <div className={`${prefixCls}-list`}>
            <Space direction="vertical" size={12}>
              {filterSuppliers.map((supplier: any) => {
                const supplier_uuid = assembleSelectedId(supplier.vendorId, supplier.tenantCode);
                return (
                  <div
                    key={supplier_uuid}
                    className={clx(`${prefixCls}-item`, {
                      [`${prefixCls}-item-active`]: supplier_uuid === selectedId,
                    })}
                    onClick={() => this.handleChoose(supplier)}
                  >
                    <div style={{ display: 'flex' }}><BuildingFilled />{supplier.full_name}</div>
                    {supplier_uuid === selectedId && <div><Check /></div>}
                  </div>
                )
              })}
            </Space>
          </div> : <Empty text="暂无搜索结果" imageNode={<NoResults />} />}
        </div>
      </Modal>
      </WithViewBaseConfigProvider>
    );
  }
}

const mapStateToProps = (state: any, ownProps: any) => {
  return { suppliersList: state.account.suppliersList };
};

const mapDispatchToProps = (dispatch: Dispatch<actions.AccountActions>) => {
  return bindActionCreators(
    {
      isOpenNotification: actions.isOpenNotification,
    },
    dispatch,
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChangeSupplierModal);
